import React, { useEffect, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { useUser } from "../../context/UserContext";
import {
  OrgWalletBalance, LedgerRow, ClaimRow, Recharge,
  getOrgWalletBalance, getOrgWalletLedger, getOrgWalletClaims, getOrgWalletRecharges,
} from "../../api/orgWallet";

const inr = (n: number) => `₹${(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const dt = (s: string | null) => (s ? new Date(s).toLocaleString("en-IN") : "—");

const OrgWalletView: React.FC = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((o) => !o);
  const { user } = useUser();
  // Same tenant-key as OrgSettlements (the fixed pattern): the logged-in org id.
  const tenantId = user?.org_id != null ? String(user.org_id) : null;

  const [bal, setBal] = useState<OrgWalletBalance | null>(null);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [recharges, setRecharges] = useState<Recharge[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      try {
        setBal(await getOrgWalletBalance(tenantId));
        setLedger(await getOrgWalletLedger(tenantId));
        setClaims(await getOrgWalletClaims(tenantId));
        setRecharges(await getOrgWalletRecharges(tenantId));
      } catch (e: any) {
        setErr(e?.response?.data?.detail || String(e));
      }
    })();
  }, [tenantId]);

  if (!tenantId) {
    return <div style={{ padding: 24 }}>No organisation context.</div>;
  }

  return (
    <div style={{ display: "flex" }}>
      <Sidebar className={isSidebarOpen ? "open" : ""} />
      <div style={{ flex: 1 }}>
        <Header onMenuClick={toggleSidebar} />
        <div style={{ padding: 24 }}>
          <h2>Org Wallet</h2>
          {err && <div style={{ color: "#c00" }}>{err}</div>}
          {bal && (
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", margin: "12px 0" }}>
              <div>Balance: <b>{inr(bal.current_balance)}</b></div>
              <div>Outstanding: <b>{inr(bal.outstanding)}</b></div>
              <div>Available: <b>{inr(bal.available_balance)}</b></div>
              <div>Mode: <b>{bal.settlement_mode}</b></div>
            </div>
          )}

          <h3>Deposits</h3>
          <table style={{ width: "100%" }}>
            <thead><tr><th>Date</th><th>Amount</th><th>Method</th><th>Ref</th><th>Status</th><th>Credited</th></tr></thead>
            <tbody>
              {recharges.map((d) => (
                <tr key={d.id} style={{ borderTop: "1px solid #eee" }}>
                  <td>{dt(d.created_at)}</td><td>{inr(d.amount)}</td><td>{d.method}</td>
                  <td>{d.reference_number || "—"}</td><td>{d.status}</td><td>{dt(d.credited_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 style={{ marginTop: 16 }}>Reimbursements to your users</h3>
          <table style={{ width: "100%" }}>
            <thead><tr><th>Date</th><th>User</th><th>Amount</th><th>State</th></tr></thead>
            <tbody>
              {claims.map((c) => (
                <tr key={c.id} style={{ borderTop: "1px solid #eee" }}>
                  <td>{dt(c.created_at)}</td><td>{c.user_name || c.user_wallet_id}</td>
                  <td>{inr(c.claim_amount)}</td><td>{c.claim_state}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 style={{ marginTop: 16 }}>Wallet ledger</h3>
          <table style={{ width: "100%" }}>
            <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Balance after</th></tr></thead>
            <tbody>
              {ledger.map((l) => (
                <tr key={l.id} style={{ borderTop: "1px solid #eee" }}>
                  <td>{dt(l.created_date)}</td><td>{l.transaction_type || "—"}</td>
                  <td>{inr(l.transaction_amount)}</td><td>{inr(l.wallet_balance_after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrgWalletView;
